import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Target, Bell, Zap, Shield, BarChart3, ArrowRight, Check, Layout as LayoutIcon, MousePointer2, Layers, Globe, CreditCard, Mail, MessageSquare, Lock, Menu, X, Twitter, Linkedin, Instagram } from 'lucide-react';
interface AgentToBossProps {
  onBack?: () => void;
}
export function AgentToBoss({
  onBack
}: AgentToBossProps) {
  const [activeFeature, setActiveFeature] = useState('contacts');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [view, setView] = useState<'home' | 'domains' | 'platform' | 'demo' | 'enterprise' | 'pricing' | 'client-dashboard' | 'crm-core' | 'automations' | 'analytics' | 'integrations' | 'about' | 'careers' | 'press' | 'contact' | 'privacy' | 'terms' | 'security'>('home');
  const [activeDashboardTab, setActiveDashboardTab] = useState('dashboard');
  // Scroll to top on view change
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [view]);
  // Disable scroll when modal is open
  useEffect(() => {
    if (videoModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [videoModalOpen]);
  const features = [{
    id: 'contacts',
    title: 'Contact Management',
    desc: 'Centralize every interaction. Our intelligent CRM automatically enriches lead data and tracks communication history across all channels.',
    image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'leads',
    title: 'Lead Tracking',
    desc: 'Visualize your funnel with precision. Move leads through custom stages and let AI predict conversion probability based on engagement.',
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200'
  }, {
    id: 'reminders',
    title: 'Instant Reminders',
    desc: 'Never miss a follow-up. Agentic triggers notify you exactly when a lead is most likely to engage, ensuring maximum ROI on every touchpoint.',
    image: 'https://images.unsplash.com/photo-1551288049-bbbda5366392?auto=format&fit=crop&q=80&w=1200'
  }];
  return <div className="min-h-screen bg-[#F3F6F9] text-[#1B264F] font-sans selection:bg-[#00A1E0] selection:text-white" data-id="element-945">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#D8E2EF] px-8 py-4" data-id="element-946">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-947">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => {
          setView('home');
          setMobileMenuOpen(false);
        }} data-id="element-948">
            <div className="w-10 h-10 bg-[#00A1E0] rounded-lg flex items-center justify-center text-white shadow-lg shadow-[#00A1E0]/20" data-id="element-949">
              <Zap size={24} fill="currentColor" data-id="element-950" />
            </div>
            <span className="text-xl font-black tracking-tight text-[#1B264F]" data-id="element-951">
              Agent<span className="text-[#00A1E0]" data-id="element-952">to</span>boss
            </span>
          </div>

          <div className="hidden md:flex items-center gap-8 text-sm font-bold text-[#1B264F]" data-id="element-953">
            <button onClick={() => setView('home')} className={`hover:text-[#00A1E0] transition-colors ${view === 'home' ? 'text-[#00A1E0]' : ''}`} data-id="element-954">
              Home
            </button>
            <button onClick={() => setView('platform')} className={`hover:text-[#00A1E0] transition-colors ${view === 'platform' ? 'text-[#00A1E0]' : ''}`} data-id="element-955">
              Platform
            </button>
            <button onClick={() => setView('domains')} className={`hover:text-[#00A1E0] transition-colors ${view === 'domains' ? 'text-[#00A1E0]' : ''}`} data-id="element-956">
              Domains & Hosting
            </button>
            <button onClick={() => setView('enterprise')} className={`hover:text-[#00A1E0] transition-colors ${view === 'enterprise' ? 'text-[#00A1E0]' : ''}`} data-id="element-957">
              Enterprise
            </button>
            <button onClick={() => setView('pricing')} className={`hover:text-[#00A1E0] transition-colors ${view === 'pricing' ? 'text-[#00A1E0]' : ''}`} data-id="element-958">
              Pricing
            </button>
          </div>

          <div className="flex items-center gap-4" data-id="element-959">
            {onBack && <button onClick={onBack} className="hidden md:block text-xs font-bold uppercase tracking-widest text-[#1B264F]/60 hover:text-[#1B264F] transition-colors mr-4" data-id="element-960">
                ← Back to Admin
              </button>}
            <button onClick={() => setView('client-dashboard')} className="hidden md:block px-6 py-2.5 border-2 border-[#00A1E0] text-[#00A1E0] rounded-full text-sm font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-961">
              Client Login
            </button>
            <button onClick={() => setView('demo')} className="hidden md:block px-6 py-2.5 bg-[#00A1E0] text-white rounded-full text-sm font-bold hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-962">
              Demo
            </button>

            {/* Mobile Menu Button */}
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 text-[#1B264F] hover:bg-[#F3F6F9] rounded-xl transition-colors" data-id="element-963">
              {mobileMenuOpen ? <X size={24} data-id="element-964" /> : <Menu size={24} data-id="element-965" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence data-id="element-966">
          {mobileMenuOpen && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="md:hidden absolute top-full left-0 w-full bg-white border-t border-[#D8E2EF] px-8 py-8 space-y-6 shadow-2xl z-50" data-id="element-967">
              <div className="flex flex-col gap-4 text-lg font-bold text-[#1B264F]" data-id="element-968">
                {[{
              id: 'home',
              label: 'Home'
            }, {
              id: 'platform',
              label: 'Platform'
            }, {
              id: 'domains',
              label: 'Domains & Hosting'
            }, {
              id: 'enterprise',
              label: 'Enterprise'
            }, {
              id: 'pricing',
              label: 'Pricing'
            }].map(item => <button key={item.id} onClick={() => {
              setView(item.id as any);
              setMobileMenuOpen(false);
            }} className={`text-left hover:text-[#00A1E0] transition-colors ${view === item.id ? 'text-[#00A1E0]' : ''}`} data-id="element-969">
                    {item.label}
                  </button>)}
              </div>

              <div className="pt-6 border-t border-[#D8E2EF] flex flex-col gap-4" data-id="element-970">
                <button onClick={() => {
              setView('client-dashboard');
              setMobileMenuOpen(false);
            }} className="w-full px-6 py-4 border-2 border-[#00A1E0] text-[#00A1E0] rounded-2xl text-base font-black hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-971">
                  Client Login
                </button>
                <button onClick={() => {
              setView('demo');
              setMobileMenuOpen(false);
            }} className="w-full px-6 py-4 bg-[#00A1E0] text-white rounded-2xl text-base font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-972">
                  Demo
                </button>
                {onBack && <button onClick={onBack} className="w-full py-4 text-sm font-bold uppercase tracking-widest text-[#1B264F]/60 hover:text-[#1B264F] transition-colors" data-id="element-973">
                    ← Back to Admin
                  </button>}
              </div>
            </motion.div>}
        </AnimatePresence>
      </nav>

      {view === 'client-dashboard' && <motion.div initial={{
      opacity: 0,
      x: 20
    }} animate={{
      opacity: 1,
      x: 0
    }} exit={{
      opacity: 0,
      x: -20
    }} transition={{
      duration: 0.4
    }} className="flex flex-col md:flex-row min-h-[calc(100vh-73px)]" data-id="element-974">
          {/* Dashboard Sidebar */}
          <div className="w-full md:w-64 bg-[#1B264F] text-white p-4 md:p-6 flex md:flex-col gap-2 overflow-x-auto md:overflow-x-visible no-scrollbar shrink-0" data-id="element-975">
            {[{
          id: 'dashboard',
          label: 'Dashboard',
          icon: LayoutIcon
        }, {
          id: 'domains',
          label: 'Domains',
          icon: Globe
        }, {
          id: 'dns',
          label: 'DNS Management',
          icon: Layers
        }, {
          id: 'billing',
          label: 'Billing',
          icon: CreditCard
        }, {
          id: 'email-marketing',
          label: 'E-mail Marketing',
          icon: Mail
        }, {
          id: 'crm',
          label: 'CRM',
          icon: Users
        }, {
          id: 'sms',
          label: 'SMS Blast',
          icon: MessageSquare
        }, {
          id: 'pro-email',
          label: 'Pro Email',
          icon: Shield
        }, {
          id: 'ssl',
          label: 'SSL Certificates',
          icon: Lock
        }].map(tab => <button key={tab.id} onClick={() => setActiveDashboardTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-bold transition-all whitespace-nowrap md:whitespace-normal ${activeDashboardTab === tab.id ? 'bg-[#00A1E0] text-white shadow-lg shadow-[#00A1E0]/20' : 'text-white/60 hover:bg-white/5 hover:text-white'}`} data-id="element-976">
                <tab.icon size={18} data-id="element-977" />
                {tab.label}
              </button>)}
          </div>

          {/* Dashboard Content */}
          <div className="flex-1 p-4 md:p-10 overflow-y-auto" data-id="element-978">
            <div className="max-w-5xl mx-auto" data-id="element-979">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4 mb-10" data-id="element-980">
                <div data-id="element-981">
                  <h2 className="text-2xl md:text-3xl font-black text-[#1B264F] uppercase tracking-tight" data-id="element-982">
                    {activeDashboardTab.replace('-', ' ')}
                  </h2>
                  <p className="text-slate-500 font-medium text-sm md:text-base" data-id="element-983">
                    Manage your enterprise assets and communications.
                  </p>
                </div>
                {activeDashboardTab === 'sms' && <button className="w-full md:w-auto px-6 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm shadow-lg shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-984">
                    Add SMS Credit
                  </button>}
              </div>

              <div className="bg-white rounded-[24px] md:rounded-[32px] border border-[#D8E2EF] shadow-sm p-4 md:p-8 min-h-[500px]" data-id="element-985">
                {activeDashboardTab === 'dashboard' && <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6" data-id="element-986">
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-987">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-988">
                        Active Domains
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-989">4</p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-990">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-991">
                        CRM Leads
                      </p>
                      <p className="text-2xl font-black text-[#00A1E0]" data-id="element-992">
                        1,284
                      </p>
                    </div>
                    <div className="p-6 bg-[#F3F6F9] rounded-2xl border border-[#D8E2EF]" data-id="element-993">
                      <p className="text-[10px] font-black uppercase text-[#1B264F]/40 mb-1" data-id="element-994">
                        SMS Balance
                      </p>
                      <p className="text-2xl font-black text-[#1B264F]" data-id="element-995">
                        $42.50
                      </p>
                    </div>
                  </div>}

                {activeDashboardTab === 'domains' && <div className="space-y-4" data-id="element-996">
                    {['agenttoboss.com', 'myagency.io', 'realtypro.net'].map(domain => <div key={domain} className="flex items-center justify-between p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-997">
                          <div className="flex items-center gap-3" data-id="element-998">
                            <Globe size={20} className="text-[#00A1E0]" data-id="element-999" />
                            <span className="font-bold text-[#1B264F]" data-id="element-1000">
                              {domain}
                            </span>
                          </div>
                          <div className="flex gap-2" data-id="element-1001">
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-1002">
                              Manage
                            </button>
                            <button className="px-4 py-2 bg-white border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-slate-50" data-id="element-1003">
                              DNS
                            </button>
                          </div>
                        </div>)}
                  </div>}

                {activeDashboardTab === 'sms' && <div className="space-y-8" data-id="element-1004">
                    <div className="p-8 bg-[#1B264F] text-white rounded-2xl relative overflow-hidden" data-id="element-1005">
                      <div className="relative z-10" data-id="element-1006">
                        <p className="text-xs font-black uppercase text-white/40 mb-2" data-id="element-1007">
                          Available Credit
                        </p>
                        <p className="text-5xl font-black" data-id="element-1008">$42.50</p>
                        <p className="text-sm text-white/60 mt-4" data-id="element-1009">
                          Estimated 2,360 SMS segments remaining.
                        </p>
                      </div>
                      <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-1010"></div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1011">
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-1012">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-1013">
                          Quick Blast
                        </h4>
                        <textarea className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl p-4 text-sm focus:outline-none min-h-[100px]" placeholder="Enter your SMS message..." data-id="element-1014" />
                        <button className="w-full mt-4 py-3 bg-[#00A1E0] text-white rounded-xl font-black text-sm" data-id="element-1015">
                          Send Blast
                        </button>
                      </div>
                      <div className="p-6 border border-[#D8E2EF] rounded-2xl" data-id="element-1016">
                        <h4 className="font-black text-[#1B264F] mb-4" data-id="element-1017">
                          Auto-Topup
                        </h4>
                        <div className="flex items-center justify-between p-3 bg-[#F3F6F9] rounded-xl" data-id="element-1018">
                          <span className="text-sm font-bold" data-id="element-1019">
                            Enable Auto-Topup
                          </span>
                          <div className="w-10 h-5 bg-[#00A1E0] rounded-full relative" data-id="element-1020">
                            <div className="absolute right-1 top-1 w-3 h-3 bg-white rounded-full" data-id="element-1021"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>}

                {/* Placeholder for other tabs */}
                {['dns', 'billing', 'email-marketing', 'crm', 'pro-email', 'ssl'].includes(activeDashboardTab) && activeDashboardTab !== 'sms' && activeDashboardTab !== 'domains' && <div className="flex flex-col items-center justify-center py-20 text-center" data-id="element-1022">
                      <div className="w-20 h-20 bg-[#F3F6F9] rounded-3xl flex items-center justify-center text-[#00A1E0] mb-6" data-id="element-1023">
                        <Zap size={40} data-id="element-1024" />
                      </div>
                      <h3 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1025">
                        Enterprise {activeDashboardTab.replace('-', ' ')}
                      </h3>
                      <p className="text-slate-500 max-w-xs mx-auto" data-id="element-1026">
                        Your {activeDashboardTab} services are active and being
                        managed by our enterprise architects.
                      </p>
                    </div>}
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'pricing' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1027">
          <div className="max-w-7xl mx-auto" data-id="element-1028">
            <div className="text-center mb-20" data-id="element-1029">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-1030">
                Transparent Comparison
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-1031">
                See how Agenttoboss stacks up against traditional CRM and
                marketing platforms.
              </p>
            </div>

            <div className="overflow-x-auto" data-id="element-1032">
              <table className="w-full border-collapse bg-white rounded-[32px] overflow-hidden shadow-2xl border border-[#D8E2EF]" data-id="element-1033">
                <thead data-id="element-1034">
                  <tr className="bg-[#1B264F] text-white" data-id="element-1035">
                    <th className="p-8 text-left text-lg font-black" data-id="element-1036">
                      Feature
                    </th>
                    <th className="p-8 text-center text-lg font-black bg-[#00A1E0]" data-id="element-1037">
                      Agenttoboss
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-1038">
                      Traditional CRMs
                    </th>
                    <th className="p-8 text-center text-lg font-black opacity-60" data-id="element-1039">
                      Marketing Agencies
                    </th>
                  </tr>
                </thead>
                <tbody className="text-[#1B264F]" data-id="element-1040">
                  {[{
                f: 'Custom 5-Pager Website',
                a: 'Included (Free)',
                b: '$2,500+ Setup',
                c: '$5,000+ Setup'
              }, {
                f: 'CRM + 100k Email Blasts',
                a: 'Included (Free)',
                b: '$150+/mo',
                c: 'N/A'
              }, {
                f: 'Social Media (4 Channels)',
                a: '$150/mo',
                b: 'N/A',
                c: '$1,000+/mo'
              }, {
                f: 'AI Telephony (Tier 2)',
                a: '$119/seat',
                b: '$250+/seat',
                c: 'N/A'
              }, {
                f: 'Review Management',
                a: 'Included',
                b: 'Add-on ($50/mo)',
                c: 'Included'
              }, {
                f: 'Business Consulting',
                a: 'Included (Advance)',
                b: 'N/A',
                c: '$200+/hr'
              }].map((row, i) => <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F3F6F9]'} data-id="element-1041">
                      <td className="p-8 font-bold border-b border-[#D8E2EF]" data-id="element-1042">
                        {row.f}
                      </td>
                      <td className="p-8 text-center font-black text-[#00A1E0] border-b border-[#D8E2EF] bg-[#00A1E0]/5" data-id="element-1043">
                        {row.a}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-1044">
                        {row.b}
                      </td>
                      <td className="p-8 text-center font-medium text-[#1B264F]/40 border-b border-[#D8E2EF]" data-id="element-1045">
                        {row.c}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            <div className="mt-20 bg-[#1B264F] rounded-[40px] p-12 text-white text-center" data-id="element-1046">
              <h2 className="text-3xl font-black mb-6" data-id="element-1047">
                Ready to switch and save?
              </h2>
              <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all" data-id="element-1048">
                Get a Custom Quote
              </button>
            </div>
          </div>
        </motion.div>}

      {/* Informational Pages */}
      {['crm-core', 'automations', 'analytics', 'integrations', 'about', 'careers', 'press', 'contact', 'privacy', 'terms', 'security'].includes(view) && <motion.div initial={{
      opacity: 0,
      scale: 0.98
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.02
    }} transition={{
      duration: 0.4
    }} className="py-24 px-8 min-h-[60vh]" data-id="element-1049">
          <div className="max-w-4xl mx-auto" data-id="element-1050">
            <div className="mb-12" data-id="element-1051">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6 capitalize tracking-tight" data-id="element-1052">
                {view.replace('-', ' ')}
              </h1>
              <div className="h-1.5 w-24 bg-[#00A1E0] rounded-full mb-8" data-id="element-1053"></div>
            </div>

            <div className="prose prose-slate prose-lg max-w-none text-[#1B264F]/70 font-medium leading-relaxed space-y-8" data-id="element-1054">
              {view === 'crm-core' && <>
                  <p data-id="element-1055">
                    Our CRM Core is the heartbeat of your agency. Built for
                    high-performance teams, it centralizes every interaction,
                    lead, and deal into a single, lightning-fast interface.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 not-prose" data-id="element-1056">
                    {[{
                title: 'Unified Inbox',
                desc: 'Every email, SMS, and call in one place.'
              }, {
                title: 'Smart Pipelines',
                desc: 'Visualize your deals with drag-and-drop ease.'
              }, {
                title: 'Lead Enrichment',
                desc: 'Automatic profile building for every new lead.'
              }, {
                title: 'Activity Tracking',
                desc: 'Never lose track of a follow-up again.'
              }].map((item, i) => <div key={i} className="p-6 bg-white rounded-2xl border border-[#D8E2EF] shadow-sm" data-id="element-1057">
                        <h4 className="font-black text-[#1B264F] mb-2" data-id="element-1058">
                          {item.title}
                        </h4>
                        <p className="text-sm" data-id="element-1059">{item.desc}</p>
                      </div>)}
                  </div>
                </>}

              {view === 'automations' && <>
                  <p data-id="element-1060">
                    Stop doing manual work. Our agentic automation engine
                    handles the repetitive tasks so you can focus on closing
                    deals.
                  </p>
                  <ul className="space-y-4 list-none pl-0" data-id="element-1061">
                    {['Automated lead distribution based on performance', 'Instant SMS/Email auto-responders', 'Trigger-based workflow sequences', 'AI-driven follow-up reminders'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-1062">
                        <div className="w-6 h-6 bg-[#00A1E0]/10 rounded-full flex items-center justify-center text-[#00A1E0]" data-id="element-1063">
                          <Check size={14} data-id="element-1064" />
                        </div>
                        {item}
                      </li>)}
                  </ul>
                </>}

              {view === 'analytics' && <>
                  <p data-id="element-1065">
                    Data-driven decisions start here. Get deep insights into
                    your funnel performance, agent productivity, and marketing
                    ROI.
                  </p>
                  <div className="h-64 bg-[#1B264F] rounded-3xl p-8 flex items-end gap-2" data-id="element-1066">
                    {[60, 40, 80, 50, 90, 70, 85].map((h, i) => <div key={i} className="flex-1 bg-[#00A1E0]/20 rounded-t-lg" style={{
                height: `${h}%`
              }} data-id="element-1067">
                        <div className="w-full bg-[#00A1E0] rounded-t-lg" style={{
                  height: '30%'
                }} data-id="element-1068"></div>
                      </div>)}
                  </div>
                </>}

              {view === 'integrations' && <>
                  <p data-id="element-1069">
                    AgentToBoss plays well with others. Connect your favorite
                    tools and create a seamless tech stack for your agency.
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 not-prose" data-id="element-1070">
                    {['Salesforce', 'Zillow', 'Slack', 'Gmail', 'Twilio', 'Stripe', 'Zapier', 'HubSpot'].map(tool => <div key={tool} className="p-4 bg-white rounded-xl border border-[#D8E2EF] text-center font-bold text-sm" data-id="element-1071">
                        {tool}
                      </div>)}
                  </div>
                </>}

              {view === 'about' && <>
                  <p data-id="element-1072">
                    Founded in 2024, AgentToBoss was built by agents, for
                    agents. We saw the limitations of traditional CRMs and
                    decided to build the command center we always wanted.
                  </p>
                  <p data-id="element-1073">
                    Our mission is to empower the top 1% of sales organizations
                    with the infrastructure they need to dominate their markets.
                  </p>
                </>}

              {view === 'careers' && <>
                  <p data-id="element-1074">
                    We're looking for the best engineers, designers, and scaling
                    specialists to help us build the future of sales technology.
                  </p>
                  <div className="space-y-4 not-prose" data-id="element-1075">
                    {['Senior Frontend Engineer', 'Product Designer', 'Customer Success Architect'].map(job => <div key={job} className="p-6 bg-white rounded-2xl border border-[#D8E2EF] flex justify-between items-center" data-id="element-1076">
                        <span className="font-bold" data-id="element-1077">{job}</span>
                        <button className="text-[#00A1E0] font-black text-sm" data-id="element-1078">
                          Apply Now →
                        </button>
                      </div>)}
                  </div>
                </>}

              {view === 'contact' && <div className="not-prose" data-id="element-1079">
                  <p className="mb-8 font-medium text-lg" data-id="element-1080">
                    Need help? Our team is available 24/7 for enterprise
                    partners.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1081">
                    <div className="space-y-4" data-id="element-1082">
                      <div className="p-6 bg-white rounded-2xl border border-[#D8E2EF]" data-id="element-1083">
                        <Mail className="text-[#00A1E0] mb-4" data-id="element-1084" />
                        <h4 className="font-black" data-id="element-1085">Email Us</h4>
                        <p className="text-sm" data-id="element-1086">support@agenttoboss.com</p>
                      </div>
                      <div className="p-6 bg-white rounded-2xl border border-[#D8E2EF]" data-id="element-1087">
                        <MessageSquare className="text-[#00A1E0] mb-4" data-id="element-1088" />
                        <h4 className="font-black" data-id="element-1089">Live Chat</h4>
                        <p className="text-sm" data-id="element-1090">Available in your dashboard</p>
                      </div>
                    </div>
                    <form className="space-y-4" data-id="element-1091">
                      <input type="text" placeholder="Name" className="w-full p-4 bg-white border border-[#D8E2EF] rounded-xl focus:outline-none focus:border-[#00A1E0]" data-id="element-1092" />
                      <textarea placeholder="Message" rows={4} className="w-full p-4 bg-white border border-[#D8E2EF] rounded-xl focus:outline-none focus:border-[#00A1E0]" data-id="element-1093" />
                      <button className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black" data-id="element-1094">
                        Send Message
                      </button>
                    </form>
                  </div>
                </div>}

              {['privacy', 'terms', 'security'].includes(view) && <div className="space-y-6" data-id="element-1095">
                  <p data-id="element-1096">Last updated: May 19, 2026</p>
                  <h3 className="text-2xl font-black text-[#1B264F]" data-id="element-1097">
                    1. Overview
                  </h3>
                  <p data-id="element-1098">
                    This document outlines our commitment to your{' '}
                    {view === 'security' ? 'data protection' : 'rights and responsibilities'}{' '}
                    when using the AgentToBoss platform.
                  </p>
                  <h3 className="text-2xl font-black text-[#1B264F]" data-id="element-1099">
                    2. Data Handling
                  </h3>
                  <p data-id="element-1100">
                    We use enterprise-grade encryption and follow strict
                    industry standards to ensure your business data remains
                    private and secure at all times.
                  </p>
                </div>}
            </div>

            <button onClick={() => setView('home')} className="mt-16 inline-flex items-center gap-2 text-[#00A1E0] font-black hover:gap-3 transition-all" data-id="element-1101">
              ← Back to Home
            </button>
          </div>
        </motion.div>}

      {view === 'enterprise' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} className="overflow-hidden" data-id="element-1102">
          {/* Enterprise Hero */}
          <section className="relative py-32 px-8 bg-[#1B264F] text-white" data-id="element-1103">
            <div className="absolute inset-0 opacity-20" data-id="element-1104">
              <img src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&q=80&w=2000" alt="Enterprise Background" className="w-full h-full object-cover" data-id="element-1105" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/80 to-transparent" data-id="element-1106"></div>
            </div>

            <div className="w-full mx-auto relative z-10 px-8" data-id="element-1107">
              <div className="max-w-3xl" data-id="element-1108">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-black uppercase tracking-widest mb-8" data-id="element-1109">
                  Enterprise Solutions
                </div>
                <h1 className="text-6xl lg:text-8xl font-black mb-8 tracking-tighter leading-[0.9]" data-id="element-1110">
                  Built for the <br data-id="element-1111" />
                  <span className="text-[#00A1E0]" data-id="element-1112">1% of Agencies.</span>
                </h1>
                <p className="text-xl text-white/60 font-medium leading-relaxed mb-10" data-id="element-1113">
                  Scale without limits. Our enterprise tier provides the
                  infrastructure, security, and custom engineering required by
                  the world's largest real estate and sales organizations.
                </p>
                <div className="flex gap-4" data-id="element-1114">
                  <button onClick={() => setView('demo')} className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30" data-id="element-1115">
                    Speak with an Architect
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Custom Integrations Section */}
          <section className="py-32 px-8 bg-white" data-id="element-1116">
            <div className="max-w-7xl mx-auto" data-id="element-1117">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-1118">
                <div data-id="element-1119">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-8 tracking-tight" data-id="element-1120">
                    Custom Engineering & <br data-id="element-1121" />
                    <span className="text-[#00A1E0]" data-id="element-1122">Deep Integrations</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-12" data-id="element-1123">
                    Off-the-shelf software doesn't cut it at your scale. Our
                    dedicated engineering team builds custom bridges between
                    Agenttoboss and your existing tech stack.
                  </p>

                  <div className="space-y-8" data-id="element-1124">
                    <div className="flex gap-6" data-id="element-1125">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1126">
                        <Zap size={28} data-id="element-1127" />
                      </div>
                      <div data-id="element-1128">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1129">
                          Enterprise Telephony
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1130">
                          Custom SIP trunking, advanced IVR flows, and global
                          carrier integrations tailored to your specific call
                          center requirements.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-1131">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1132">
                        <Layers size={28} data-id="element-1133" />
                      </div>
                      <div data-id="element-1134">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1135">
                          Proprietary AI Workflows
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1136">
                          We deploy custom LLM agents trained on your unique
                          business data to automate lead qualification and
                          complex back-office tasks.
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-6" data-id="element-1137">
                      <div className="w-14 h-14 bg-[#F3F6F9] rounded-2xl flex items-center justify-center text-[#00A1E0] shrink-0" data-id="element-1138">
                        <Shield size={28} data-id="element-1139" />
                      </div>
                      <div data-id="element-1140">
                        <h4 className="text-xl font-black text-[#1B264F] mb-2" data-id="element-1141">
                          SSO & Governance
                        </h4>
                        <p className="text-[#1B264F]/60 font-medium" data-id="element-1142">
                          Enterprise-grade security with SAML/SSO, custom RBAC,
                          and comprehensive audit logging for compliance-heavy
                          environments.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-1143">
                  <div className="bg-[#1B264F] rounded-[40px] p-12 shadow-2xl relative z-10" data-id="element-1144">
                    <div className="space-y-6" data-id="element-1145">
                      <div className="flex items-center justify-between p-4 bg-white/5 border border-white/10 rounded-2xl" data-id="element-1146">
                        <span className="text-white/60 font-bold" data-id="element-1147">
                          API Status
                        </span>
                        <span className="text-[#00A1E0] font-black" data-id="element-1148">
                          99.99% Uptime
                        </span>
                      </div>
                      <div className="h-48 bg-[#F3F6F9]/5 rounded-2xl border border-white/10 p-6" data-id="element-1149">
                        <div className="flex gap-2 mb-4" data-id="element-1150">
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]" data-id="element-1151"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-1152"></div>
                          <div className="w-2 h-2 rounded-full bg-[#00A1E0]/40" data-id="element-1153"></div>
                        </div>
                        <div className="space-y-2" data-id="element-1154">
                          <div className="h-2 w-3/4 bg-white/10 rounded" data-id="element-1155"></div>
                          <div className="h-2 w-1/2 bg-white/10 rounded" data-id="element-1156"></div>
                          <div className="h-2 w-2/3 bg-white/10 rounded" data-id="element-1157"></div>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4" data-id="element-1158">
                        <div className="p-4 bg-[#00A1E0] rounded-2xl text-center" data-id="element-1159">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-1160">
                            Endpoints
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-1161">450+</p>
                        </div>
                        <div className="p-4 bg-white/5 border border-white/10 rounded-2xl text-center" data-id="element-1162">
                          <p className="text-[10px] font-black uppercase text-white/60 mb-1" data-id="element-1163">
                            Latency
                          </p>
                          <p className="text-xl font-black text-white" data-id="element-1164">12ms</p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Decorative Glow */}
                  <div className="absolute -top-10 -right-10 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full" data-id="element-1165"></div>
                </div>
              </div>
            </div>
          </section>

          {/* Enterprise CTA Form */}
          <section className="py-32 px-8 bg-[#F3F6F9]" data-id="element-1166">
            <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl p-12" data-id="element-1167">
              <div className="text-center mb-12" data-id="element-1168">
                <h2 className="text-4xl font-black text-[#1B264F] mb-4" data-id="element-1169">
                  Request Enterprise Access
                </h2>
                <p className="text-[#1B264F]/60 font-medium" data-id="element-1170">
                  Our enterprise architects will reach out within 2 hours.
                </p>
              </div>
              <form className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1171">
                <div className="space-y-2" data-id="element-1172">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1173">
                    Company Name
                  </label>
                  <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Global Realty Group" data-id="element-1174" />
                </div>
                <div className="space-y-2" data-id="element-1175">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1176">
                    Estimated Seats
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-1177">
                    <option data-id="element-1178">50-100 seats</option>
                    <option data-id="element-1179">100-500 seats</option>
                    <option data-id="element-1180">500+ seats</option>
                  </select>
                </div>
                <div className="space-y-2" data-id="element-1181">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1182">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="vp@globalrealty.com" data-id="element-1183" />
                </div>
                <div className="space-y-2" data-id="element-1184">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1185">
                    Request Type
                  </label>
                  <select className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-1186">
                    <option data-id="element-1187">Book a Demo</option>
                    <option data-id="element-1188">Request Callback</option>
                    <option data-id="element-1189">Custom Integration Quote</option>
                  </select>
                </div>
                <button className="md:col-span-2 py-5 bg-[#00A1E0] text-white rounded-xl font-black text-lg shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-1190">
                  Submit Enterprise Request
                </button>
              </form>
            </div>
          </section>
        </motion.div>}

      {view === 'platform' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1191">
          <div className="max-w-7xl mx-auto" data-id="element-1192">
            <div className="mb-16" data-id="element-1193">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6" data-id="element-1194">
                The Scaling Engine
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-3xl font-medium" data-id="element-1195">
                Our platform is designed to eliminate the technical overhead of
                running a high-performance agency. Here is how we enable you to
                scale.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-1196">
              <div className="space-y-8" data-id="element-1197">
                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-1198">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1199">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-1200">
                      <LayoutIcon size={24} data-id="element-1201" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-1202">Websites & Hosting</h3>
                  </div>
                  <div className="space-y-6" data-id="element-1203">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-1204">
                      <div data-id="element-1205">
                        <p className="font-bold text-[#1B264F]" data-id="element-1206">
                          5-Pager Standard Website
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1207">
                          Free per our standards
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1208">
                        $20/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-1209">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-1210">
                      <div data-id="element-1211">
                        <p className="font-bold text-[#1B264F]" data-id="element-1212">
                          Enterprise (10+ Pages)
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1213">
                          Custom high-capacity architecture
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1214">
                        $65/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-1215">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-1216">
                      <div data-id="element-1217">
                        <p className="font-bold text-[#1B264F]" data-id="element-1218">
                          E-commerce Solution
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1219">
                          Full store integration
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1220">
                        $70/mo{' '}
                        <span className="text-[10px] text-[#1B264F]/40 block text-right" data-id="element-1221">
                          Hosting (6mo Retainer)
                        </span>
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-1222">
                  <div className="flex items-center gap-4 mb-6" data-id="element-1223">
                    <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-2xl flex items-center justify-center text-[#00A1E0]" data-id="element-1224">
                      <Target size={24} data-id="element-1225" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-1226">Marketing & CRM</h3>
                  </div>
                  <div className="space-y-6" data-id="element-1227">
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-1228">
                      <div data-id="element-1229">
                        <p className="font-bold text-[#1B264F]" data-id="element-1230">
                          Social Media Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1231">
                          4 Accounts + GMB/Yelp Setup + Review Mgmt
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1232">$150/mo</p>
                    </div>
                    <div className="flex justify-between items-start pb-4 border-b border-[#F3F6F9]" data-id="element-1233">
                      <div data-id="element-1234">
                        <p className="font-bold text-[#1B264F]" data-id="element-1235">
                          CRM & Email Marketing
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1236">
                          First 100,000 email blasts per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1237">FREE</p>
                    </div>
                    <div className="flex justify-between items-start" data-id="element-1238">
                      <div data-id="element-1239">
                        <p className="font-bold text-[#1B264F]" data-id="element-1240">
                          High Volume Email
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1241">
                          Up to 1M email sends per month
                        </p>
                      </div>
                      <p className="font-black text-[#00A1E0]" data-id="element-1242">+$15/mo</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-8" data-id="element-1243">
                <div className="bg-[#1B264F] text-white p-8 rounded-3xl shadow-2xl relative overflow-hidden" data-id="element-1244">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#00A1E0]/20 blur-[60px] rounded-full" data-id="element-1245"></div>
                  <div className="flex items-center gap-4 mb-8 relative z-10" data-id="element-1246">
                    <div className="w-12 h-12 bg-[#00A1E0] rounded-2xl flex items-center justify-center text-white" data-id="element-1247">
                      <Zap size={24} fill="currentColor" data-id="element-1248" />
                    </div>
                    <h3 className="text-2xl font-black" data-id="element-1249">Phone Systems</h3>
                  </div>
                  <div className="space-y-8 relative z-10" data-id="element-1250">
                    <div className="p-6 bg-white/5 border border-white/10 rounded-2xl" data-id="element-1251">
                      <div className="flex justify-between items-center mb-4" data-id="element-1252">
                        <h4 className="font-black text-[#00A1E0]" data-id="element-1253">
                          Tier 1: Basic
                        </h4>
                        <p className="text-2xl font-black" data-id="element-1254">
                          $35
                          <span className="text-xs text-white/40" data-id="element-1255">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/60 mb-4" data-id="element-1256">
                        Voice + CRM Integration. Perfect for small teams.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/40" data-id="element-1257">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="p-6 bg-[#00A1E0] rounded-2xl shadow-xl" data-id="element-1258">
                      <div className="flex justify-between items-center mb-4" data-id="element-1259">
                        <h4 className="font-black text-white" data-id="element-1260">
                          Tier 2: AI Advanced
                        </h4>
                        <p className="text-2xl font-black" data-id="element-1261">
                          $119
                          <span className="text-xs text-white/70" data-id="element-1262">
                            /seat/mo
                          </span>
                        </p>
                      </div>
                      <p className="text-sm text-white/90 mb-4" data-id="element-1263">
                        Unlimited Voice + AI + Automation + Dedicated Support
                        Engineer.
                      </p>
                      <div className="text-[10px] font-bold uppercase tracking-widest text-white/70" data-id="element-1264">
                        Onboarding: $35 Porting Fee
                      </div>
                    </div>
                    <div className="pt-4 border-t border-white/10" data-id="element-1265">
                      <div className="flex justify-between text-xs font-bold" data-id="element-1266">
                        <span className="text-white/60" data-id="element-1267">SMS Segment</span>
                        <span data-id="element-1268">$0.018</span>
                      </div>
                      <div className="flex justify-between text-xs font-bold mt-2" data-id="element-1269">
                        <span className="text-white/60" data-id="element-1270">MMS Segment</span>
                        <span data-id="element-1271">$0.050</span>
                      </div>
                      <p className="text-[10px] text-white/40 mt-4 italic text-center" data-id="element-1272">
                        *Min $20 wallet required for SMS enablement
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-8 rounded-3xl border border-[#D8E2EF] shadow-sm" data-id="element-1273">
                  <h3 className="text-xl font-black mb-6" data-id="element-1274">
                    Add-ons & Essentials
                  </h3>
                  <div className="grid grid-cols-2 gap-4" data-id="element-1275">
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-1276">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-1277">
                        SSL Certificate
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-1278">
                        $30/year
                      </p>
                    </div>
                    <div className="p-4 bg-[#F3F6F9] rounded-2xl" data-id="element-1279">
                      <p className="text-[10px] font-black text-[#1B264F]/40 uppercase mb-1" data-id="element-1280">
                        Professional Email
                      </p>
                      <p className="text-lg font-black text-[#1B264F]" data-id="element-1281">
                        5 FREE
                      </p>
                      <p className="text-[10px] text-[#1B264F]/40 font-bold" data-id="element-1282">
                        $3/extra
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'demo' && <motion.div initial={{
      opacity: 0,
      scale: 0.95
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0,
      scale: 1.05
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1283">
          <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-[#D8E2EF] shadow-2xl overflow-hidden flex flex-col md:flex-row" data-id="element-1284">
            <div className="md:w-1/2 bg-[#1B264F] p-12 text-white flex flex-col justify-between" data-id="element-1285">
              <div data-id="element-1286">
                <h2 className="text-4xl font-black mb-6" data-id="element-1287">
                  Experience the Power.
                </h2>
                <p className="text-white/60 font-medium leading-relaxed mb-8" data-id="element-1288">
                  Schedule a 15-minute walkthrough of the Agenttoboss platform
                  and see how we can automate your growth.
                </p>
                <div className="space-y-6" data-id="element-1289">
                  <div className="flex items-center gap-4" data-id="element-1290">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-1291">
                      <Check size={20} data-id="element-1292" />
                    </div>
                    <span className="font-bold" data-id="element-1293">Live CRM Walkthrough</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1294">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-1295">
                      <Check size={20} data-id="element-1296" />
                    </div>
                    <span className="font-bold" data-id="element-1297">Automation Strategy</span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-1298">
                    <div className="w-10 h-10 bg-[#00A1E0] rounded-xl flex items-center justify-center" data-id="element-1299">
                      <Check size={20} data-id="element-1300" />
                    </div>
                    <span className="font-bold" data-id="element-1301">Custom Pricing Quote</span>
                  </div>
                </div>
              </div>
              <div className="pt-12 border-t border-white/10" data-id="element-1302">
                <p className="text-xs font-bold text-white/40 uppercase tracking-widest" data-id="element-1303">
                  Trusted by 10,000+ Agents
                </p>
              </div>
            </div>
            <div className="md:w-1/2 p-12" data-id="element-1304">
              <h3 className="text-2xl font-black mb-8" data-id="element-1305">Schedule Your Demo</h3>
              <form className="space-y-6" data-id="element-1306">
                <div className="grid grid-cols-2 gap-4" data-id="element-1307">
                  <div className="space-y-2" data-id="element-1308">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1309">
                      First Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="John" data-id="element-1310" />
                  </div>
                  <div className="space-y-2" data-id="element-1311">
                    <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1312">
                      Last Name
                    </label>
                    <input type="text" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="Doe" data-id="element-1313" />
                  </div>
                </div>
                <div className="space-y-2" data-id="element-1314">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1315">
                    Work Email
                  </label>
                  <input type="email" className="w-full bg-[#F3F6F9] border border-[#D8E2EF] rounded-xl px-4 py-3 focus:outline-none focus:border-[#00A1E0]" placeholder="john@agency.com" data-id="element-1316" />
                </div>
                <div className="space-y-2" data-id="element-1317">
                  <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1318">
                    Select Date
                  </label>
                  <div className="grid grid-cols-3 gap-2" data-id="element-1319">
                    {['May 15', 'May 16', 'May 17'].map(d => <button key={d} type="button" className="py-2 bg-[#F3F6F9] border border-[#D8E2EF] rounded-lg text-xs font-bold hover:bg-[#00A1E0] hover:text-white transition-all" data-id="element-1320">
                        {d}
                      </button>)}
                  </div>
                </div>
                <button type="submit" className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black shadow-xl shadow-[#00A1E0]/20 hover:bg-[#0089C4] transition-all" data-id="element-1321">
                  Confirm Meeting
                </button>
              </form>
            </div>
          </div>
        </motion.div>}

      {view === 'domains' && <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0,
      y: -20
    }} transition={{
      duration: 0.4
    }} className="py-20 px-8" data-id="element-1322">
          <div className="max-w-7xl mx-auto" data-id="element-1323">
            <div className="text-center mb-16" data-id="element-1324">
              <h1 className="text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-1325">
                Find Your Digital Home
              </h1>
              <p className="text-xl text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-1326">
                Secure your domain and choose a hosting plan built for
                enterprise performance.
              </p>
            </div>

            {/* Domain Search */}
            <div className="max-w-3xl mx-auto mb-24" data-id="element-1327">
              <div className="bg-white p-2 rounded-2xl shadow-2xl border border-[#D8E2EF] flex items-center gap-2" data-id="element-1328">
                <div className="pl-6 text-[#1B264F]/30" data-id="element-1329">
                  <LayoutIcon size={24} data-id="element-1330" />
                </div>
                <input type="text" placeholder="search-your-dream-domain.com" className="flex-1 bg-transparent px-4 py-5 text-lg font-bold text-[#1B264F] focus:outline-none" data-id="element-1331" />
                <button className="px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-1332">
                  Search
                </button>
              </div>
              <div className="mt-6 flex justify-center gap-8 text-sm font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-1333">
                <span data-id="element-1334">.com $12.99</span>
                <span data-id="element-1335">.net $14.99</span>
                <span data-id="element-1336">.org $15.99</span>
                <span data-id="element-1337">.ai $59.99</span>
              </div>
            </div>

            {/* Hosting Packages */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1338">
              <div className="p-10 bg-white border border-[#D8E2EF] rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-1339">
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-1340">
                  Starter
                </h3>
                <div className="mb-8" data-id="element-1341">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-1342">
                    $29
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-1343">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-1344">
                  {['5 Pager Website', 'CRM Integration', 'Standard SSL', '24/7 Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-1345">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-1346" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#F3F6F9] text-[#1B264F] rounded-xl font-black hover:bg-[#D8E2EF] transition-all" data-id="element-1347">
                  Select Starter
                </button>
              </div>

              <div className="p-10 bg-white border-2 border-[#00A1E0] rounded-3xl shadow-2xl relative scale-105" data-id="element-1348">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#00A1E0] text-white text-[10px] font-black uppercase tracking-widest rounded-full" data-id="element-1349">
                  Recommended
                </div>
                <h3 className="text-2xl font-black text-[#1B264F] mb-2" data-id="element-1350">Pro</h3>
                <div className="mb-8" data-id="element-1351">
                  <span className="text-4xl font-black text-[#1B264F]" data-id="element-1352">
                    $79
                  </span>
                  <span className="text-[#1B264F]/40 font-bold ml-2" data-id="element-1353">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-1354">
                  {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority Support'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-[#1B264F]/70" data-id="element-1355">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-1356" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20" data-id="element-1357">
                  Select Pro
                </button>
              </div>

              <div className="p-10 bg-[#1B264F] text-white rounded-3xl shadow-sm hover:shadow-xl transition-all" data-id="element-1358">
                <h3 className="text-2xl font-black mb-2" data-id="element-1359">Advance</h3>
                <div className="mb-8" data-id="element-1360">
                  <span className="text-4xl font-black" data-id="element-1361">$199</span>
                  <span className="text-white/40 font-bold ml-2" data-id="element-1362">/mo</span>
                </div>
                <ul className="space-y-4 mb-10" data-id="element-1363">
                  {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold text-white/80" data-id="element-1364">
                      <Check size={18} className="text-[#00A1E0]" data-id="element-1365" /> {f}
                    </li>)}
                </ul>
                <button className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-black transition-all" data-id="element-1366">
                  Select Advance
                </button>
              </div>
            </div>
          </div>
        </motion.div>}

      {view === 'home' && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.5
    }} data-id="element-1367">
          {/* Hero Section */}
          <section className="relative pt-10 pb-20 px-8 overflow-hidden min-h-[80vh] flex items-center" data-id="element-1368">
            {/* Background Image with Overlay */}
            <div className="absolute inset-0 z-0" data-id="element-1369">
              <img src="https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&q=80&w=2000" alt="Real Estate Agent" className="w-full h-full object-cover" data-id="element-1370" />
              <div className="absolute inset-0 bg-gradient-to-r from-[#1B264F] via-[#1B264F]/90 to-[#1B264F]/40" data-id="element-1371"></div>
            </div>

            <div className="w-full grid grid-cols-1 lg:grid-cols-[1.2fr,0.8fr] gap-16 items-center relative z-10" data-id="element-1372">
              <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.8
          }} data-id="element-1373">
                <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#00A1E0]/20 border border-[#00A1E0]/30 rounded-full text-[#00A1E0] text-[10px] font-bold uppercase tracking-widest mb-6" data-id="element-1374">
                  <span className="relative flex h-2 w-2" data-id="element-1375">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00A1E0] opacity-75" data-id="element-1376"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#00A1E0]" data-id="element-1377"></span>
                  </span>
                  Next-Gen CRM for High-Performers
                </div>
                <h1 className="text-4xl lg:text-7xl font-black text-white leading-[0.9] mb-8 tracking-tighter" data-id="element-1378">
                  Organize.
                  <br data-id="element-1379" />
                  Scale.
                  <br data-id="element-1380" />
                  <span className="text-[#00A1E0]" data-id="element-1381">Dominate.</span>
                </h1>
                <p className="text-xl text-white/70 max-w-xl mb-10 leading-relaxed font-medium" data-id="element-1382">
                  The enterprise-grade command center for agents who refuse to
                  settle. Automate your workflow, track every lead, and crush
                  your quotas with Agenttoboss.
                </p>
                <div className="flex flex-col sm:flex-row items-center gap-4" data-id="element-1383">
                  <button onClick={() => setView('demo')} className="w-full sm:w-auto px-10 py-5 bg-[#00A1E0] text-white rounded-xl font-bold text-lg hover:bg-[#0089C4] transition-all shadow-2xl shadow-[#00A1E0]/30 flex items-center justify-center gap-2 group" data-id="element-1384">
                    Demo{' '}
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" data-id="element-1385" />
                  </button>
                  <button onClick={() => setVideoModalOpen(true)} className="w-full sm:w-auto px-10 py-5 bg-white text-[#1B264F] border border-[#D8E2EF] rounded-xl font-bold text-lg hover:bg-[#F3F6F9] transition-all" data-id="element-1386">
                    Watch Video
                  </button>
                </div>
              </motion.div>

              <motion.div initial={{
            opacity: 0,
            scale: 0.9,
            rotateY: -10
          }} whileInView={{
            opacity: 1,
            scale: 1,
            rotateY: 0
          }} viewport={{
            once: true,
            delay: 0.2
          }} transition={{
            duration: 0.8,
            delay: 0.2
          }} className="relative perspective-1000" data-id="element-1387">
                <div className="bg-white rounded-2xl shadow-[0_32px_64px_-12px_rgba(27,38,79,0.2)] border border-[#D8E2EF] p-6 overflow-hidden" data-id="element-1388">
                  <div className="flex items-center justify-between mb-8" data-id="element-1389">
                    <div className="flex gap-1.5" data-id="element-1390">
                      <div className="w-3 h-3 rounded-full bg-[#FF5F57]" data-id="element-1391"></div>
                      <div className="w-3 h-3 rounded-full bg-[#FFBD2E]" data-id="element-1392"></div>
                      <div className="w-3 h-3 rounded-full bg-[#28C840]" data-id="element-1393"></div>
                    </div>
                    <div className="px-3 py-1 bg-[#F3F6F9] rounded-md text-[10px] font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-1394">
                      Lead Funnel v2.4
                    </div>
                  </div>

                  <div className="space-y-6" data-id="element-1395">
                    <div data-id="element-1396">
                      <div className="flex justify-between mb-2" data-id="element-1397">
                        <span className="text-xs font-bold text-[#1B264F]" data-id="element-1398">
                          Active Lead Funnel
                        </span>
                        <span className="text-xs font-bold text-[#00A1E0]" data-id="element-1399">
                          84% Efficiency
                        </span>
                      </div>
                      <div className="h-24 flex items-end gap-1" data-id="element-1400">
                        {[40, 70, 45, 90, 65, 80, 55, 95, 75, 85].map((h, i) => <motion.div key={i} initial={{
                      height: 0
                    }} animate={{
                      height: `${h}%`
                    }} transition={{
                      delay: 0.5 + i * 0.05
                    }} className={`flex-1 rounded-t-sm ${i === 7 ? 'bg-[#00A1E0]' : 'bg-[#00A1E0]/20'}`} data-id="element-1401" />)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4" data-id="element-1402">
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-1403">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-1404">
                          New Leads
                        </p>
                        <p className="text-2xl font-black text-[#1B264F]" data-id="element-1405">
                          1,284
                        </p>
                      </div>
                      <div className="p-4 bg-[#F3F6F9] rounded-xl border border-[#D8E2EF]" data-id="element-1406">
                        <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase mb-1" data-id="element-1407">
                          Conversion
                        </p>
                        <p className="text-2xl font-black text-[#00A1E0]" data-id="element-1408">
                          +12.4%
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3" data-id="element-1409">
                      <p className="text-[10px] font-bold text-[#1B264F]/40 uppercase" data-id="element-1410">
                        Active Reminders
                      </p>
                      {[{
                    title: 'Follow up with Sarah J.',
                    time: 'In 12 mins',
                    icon: Users
                  }, {
                    title: 'Quarterly Review - Acme Corp',
                    time: 'In 2 hours',
                    icon: BarChart3
                  }].map((rem, i) => <div key={i} className="flex items-center justify-between p-3 bg-white border border-[#D8E2EF] rounded-lg shadow-sm" data-id="element-1411">
                          <div className="flex items-center gap-3" data-id="element-1412">
                            <div className="w-8 h-8 bg-[#00A1E0]/10 rounded flex items-center justify-center text-[#00A1E0]" data-id="element-1413">
                              <rem.icon size={16} data-id="element-1414" />
                            </div>
                            <span className="text-xs font-bold" data-id="element-1415">
                              {rem.title}
                            </span>
                          </div>
                          <span className="text-[10px] font-bold text-[#00A1E0]" data-id="element-1416">
                            {rem.time}
                          </span>
                        </div>)}
                    </div>
                  </div>
                </div>

                {/* Decorative Glow */}
                <div className="absolute -z-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-[#00A1E0]/5 blur-[100px] rounded-full" data-id="element-1417"></div>
              </motion.div>
            </div>
          </section>

          {/* Process Grid */}
          <section className="py-24 px-8 bg-white border-y border-[#D8E2EF]" data-id="element-1418">
            <div className="max-w-7xl mx-auto" data-id="element-1419">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-1420">
                {[{
              title: 'Automated Sourcing',
              desc: 'AI-driven lead discovery across 50+ platforms.',
              icon: Target,
              image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Smart Enrichment',
              desc: 'Instant data validation and profile building.',
              icon: Layers,
              image: 'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Agentic Outreach',
              desc: 'Personalized sequences that sound human.',
              icon: MousePointer2,
              image: 'https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800'
            }, {
              title: 'Real-time Analytics',
              desc: 'Deep insights into every stage of your funnel.',
              icon: BarChart3,
              image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800'
            }].map((card, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 30
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} whileHover={{
              y: -8
            }} className="bg-white border border-[#1B264F]/10 rounded-[12px] hover:border-[#00A1E0] hover:shadow-xl hover:shadow-[#00A1E0]/5 transition-all group overflow-hidden" data-id="element-1421">
                    <div className="aspect-video w-full overflow-hidden" data-id="element-1422">
                      <img src={card.image} alt={card.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-1423" />
                    </div>
                    <div className="p-8" data-id="element-1424">
                      <div className="w-12 h-12 bg-[#00A1E0]/10 rounded-lg flex items-center justify-center text-[#00A1E0] mb-6 group-hover:bg-[#00A1E0] group-hover:text-white transition-colors" data-id="element-1425">
                        <card.icon size={24} data-id="element-1426" />
                      </div>
                      <h3 className="text-lg font-black text-[#1B264F] mb-3" data-id="element-1427">
                        {card.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 leading-relaxed font-medium" data-id="element-1428">
                        {card.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </section>

          {/* CRM Feature Bento - Progressive Disclosure */}
          <motion.section initial={{
        opacity: 0,
        y: 40
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="py-32 px-8" data-id="element-1429">
            <div className="max-w-7xl mx-auto" data-id="element-1430">
              <div className="text-center mb-20" data-id="element-1431">
                <h2 className="text-4xl md:text-5xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-1432">
                  The CRM Feature Bento
                </h2>
                <p className="text-lg text-[#1B264F]/60 max-w-2xl mx-auto font-medium" data-id="element-1433">
                  Everything you need to manage your empire, built with the
                  precision of the Salesforce Lightning system.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-[350px,1fr] gap-12 items-start" data-id="element-1434">
                <div className="space-y-4" data-id="element-1435">
                  {features.map(f => <button key={f.id} onClick={() => setActiveFeature(f.id)} className={`w-full text-left p-6 rounded-2xl border transition-all ${activeFeature === f.id ? 'bg-white border-[#00A1E0] shadow-xl shadow-[#00A1E0]/5 ring-1 ring-[#00A1E0]' : 'bg-transparent border-transparent hover:bg-white/50 hover:border-[#D8E2EF]'}`} data-id="element-1436">
                      <h3 className={`text-lg font-black mb-2 ${activeFeature === f.id ? 'text-[#00A1E0]' : 'text-[#1B264F]'}`} data-id="element-1437">
                        {f.title}
                      </h3>
                      <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-1438">
                        {f.desc.split('.')[0]}.
                      </p>
                    </button>)}
                </div>

                <div className="relative bg-white rounded-3xl border border-[#D8E2EF] shadow-2xl overflow-hidden aspect-[16/10]" data-id="element-1439">
                  <AnimatePresence mode="wait" data-id="element-1440">
                    <motion.div key={activeFeature} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.4
                }} className="absolute inset-0 p-8" data-id="element-1441">
                      <div className="h-full w-full rounded-xl overflow-hidden relative group" data-id="element-1442">
                        <img src={features.find(f => f.id === activeFeature)?.image} alt={activeFeature} className="w-full h-full object-cover" data-id="element-1443" />
                        <div className="absolute inset-0 bg-gradient-to-t from-[#1B264F]/80 to-transparent flex items-end p-12" data-id="element-1444">
                          <div className="max-w-xl" data-id="element-1445">
                            <h4 className="text-3xl font-black text-white mb-4" data-id="element-1446">
                              {features.find(f => f.id === activeFeature)?.title}
                            </h4>
                            <p className="text-white/80 font-medium leading-relaxed" data-id="element-1447">
                              {features.find(f => f.id === activeFeature)?.desc}
                            </p>
                          </div>
                        </div>

                        {/* Agentic Glow Effect */}
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full pointer-events-none" data-id="element-1448">
                          <div className="absolute top-0 right-0 w-64 h-64 bg-[#00A1E0]/20 blur-[100px] rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-1000" data-id="element-1449"></div>
                        </div>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Pricing Section */}
          <section className="py-32 px-8 bg-[#1B264F] text-white overflow-hidden relative" data-id="element-1450">
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none opacity-10" data-id="element-1451">
              <div className="absolute top-[-10%] right-[-10%] w-[60%] h-[60%] bg-[#00A1E0] blur-[150px] rounded-full" data-id="element-1452"></div>
              <div className="absolute bottom-[-10%] left-[-10%] w-[40%] h-[40%] bg-[#00A1E0] blur-[120px] rounded-full" data-id="element-1453"></div>
            </div>

            <div className="max-w-7xl mx-auto relative z-10" data-id="element-1454">
              <div className="text-center mb-20" data-id="element-1455">
                <h2 className="text-4xl md:text-5xl font-black mb-6 tracking-tight" data-id="element-1456">
                  Simple, Enterprise Pricing
                </h2>
                <p className="text-lg text-white/60 max-w-2xl mx-auto font-medium" data-id="element-1457">
                  Choose the plan that fits your scale. No hidden fees, just
                  pure performance.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-id="element-1458">
                {/* Starter Plan */}
                <motion.div initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6
            }} className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-1459">
                  <h3 className="text-xl font-black mb-2" data-id="element-1460">Starter</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-1461">
                    For solo agents getting started.
                  </p>
                  <div className="mb-10" data-id="element-1462">
                    <span className="text-5xl font-black" data-id="element-1463">$99</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-1464">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-1465">
                    {['5 Pager Website', 'CRM Integration', 'Standard SSL', 'Basic Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-1466">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-1467" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-1468">
                    Demo
                  </button>
                </motion.div>

                {/* Pro Plan - Highlighted */}
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="p-1 bg-[#00A1E0] rounded-[32px] shadow-2xl shadow-[#00A1E0]/20 transform lg:scale-110 relative" data-id="element-1469">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 px-4 py-1.5 bg-white text-[#00A1E0] text-[10px] font-black uppercase tracking-[0.2em] rounded-full shadow-xl" data-id="element-1470">
                    Most Popular
                  </div>
                  <div className="p-10 bg-[#1B264F] rounded-[28px]" data-id="element-1471">
                    <h3 className="text-2xl font-black mb-2 text-[#00A1E0]" data-id="element-1472">
                      Pro
                    </h3>
                    <p className="text-white/60 text-sm font-medium mb-8" data-id="element-1473">
                      For high-performers and small teams.
                    </p>
                    <div className="mb-10" data-id="element-1474">
                      <span className="text-6xl font-black" data-id="element-1475">$249</span>
                      <span className="text-white/40 font-bold ml-2" data-id="element-1476">/mo</span>
                    </div>
                    <ul className="space-y-4 mb-10" data-id="element-1477">
                      {['Social Media Marketing', 'Review Management', 'Email Marketing', 'Priority 24/7 Support', 'Advanced Analytics'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-bold" data-id="element-1478">
                          <Check size={18} className="text-[#00A1E0]" data-id="element-1479" /> {f}
                        </li>)}
                    </ul>
                    <button onClick={() => setView('demo')} className="w-full py-5 bg-[#00A1E0] hover:bg-[#0089C4] text-white rounded-xl font-black text-lg transition-all shadow-xl shadow-[#00A1E0]/20" data-id="element-1480">
                      Go Pro Mode
                    </button>
                  </div>
                </motion.div>

                {/* Advance Plan */}
                <motion.div initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="p-10 bg-white/5 border border-white/10 rounded-3xl backdrop-blur-sm" data-id="element-1481">
                  <h3 className="text-xl font-black mb-2" data-id="element-1482">Advance</h3>
                  <p className="text-white/60 text-sm font-medium mb-8" data-id="element-1483">
                    For large agencies and brokerage.
                  </p>
                  <div className="mb-10" data-id="element-1484">
                    <span className="text-5xl font-black" data-id="element-1485">$899</span>
                    <span className="text-white/40 font-bold ml-2" data-id="element-1486">/mo</span>
                  </div>
                  <ul className="space-y-4 mb-10" data-id="element-1487">
                    {['E-commerce + CRM', 'Social Media & Reviews', 'Business Consulting', '5 Professional Emails', 'SSL & E-marketing'].map((f, i) => <li key={i} className="flex items-center gap-3 text-sm font-medium text-white/80" data-id="element-1488">
                        <Check size={16} className="text-[#00A1E0]" data-id="element-1489" /> {f}
                      </li>)}
                  </ul>
                  <button onClick={() => setView('demo')} className="w-full py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl font-bold transition-all" data-id="element-1490">
                    Demo
                  </button>
                </motion.div>
              </div>
            </div>
          </section>

          {/* Contact / Callback Section */}
          <motion.section initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="py-16 px-8 bg-white" data-id="element-1491">
            <div className="max-w-7xl mx-auto" data-id="element-1492">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center" data-id="element-1493">
                <div data-id="element-1494">
                  <h2 className="text-4xl font-black text-[#1B264F] mb-6 tracking-tight" data-id="element-1495">
                    Request a <span className="text-[#00A1E0]" data-id="element-1496">Callback</span>
                  </h2>
                  <p className="text-lg text-[#1B264F]/60 font-medium leading-relaxed mb-8" data-id="element-1497">
                    Have specific questions about our platform or need help
                    choosing the right plan? Our team of experts is ready to
                    help you scale.
                  </p>
                  <div className="space-y-6" data-id="element-1498">
                    <div className="flex items-center gap-4" data-id="element-1499">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-1500">
                        <Zap size={24} data-id="element-1501" />
                      </div>
                      <div data-id="element-1502">
                        <p className="font-black text-[#1B264F]" data-id="element-1503">
                          Instant Response
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1504">
                          Average callback time: 15 minutes
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4" data-id="element-1505">
                      <div className="w-12 h-12 bg-[#F3F6F9] rounded-xl flex items-center justify-center text-[#00A1E0]" data-id="element-1506">
                        <Shield size={24} data-id="element-1507" />
                      </div>
                      <div data-id="element-1508">
                        <p className="font-black text-[#1B264F]" data-id="element-1509">
                          Expert Guidance
                        </p>
                        <p className="text-sm text-[#1B264F]/60" data-id="element-1510">
                          Speak directly with a scaling specialist
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#F3F6F9] p-10 rounded-[40px] border border-[#D8E2EF] shadow-inner" data-id="element-1511">
                  <form className="space-y-6" data-id="element-1512">
                    <div className="space-y-2" data-id="element-1513">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1514">
                        Full Name
                      </label>
                      <input type="text" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="Jane Smith" data-id="element-1515" />
                    </div>
                    <div className="space-y-2" data-id="element-1516">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1517">
                        Phone Number
                      </label>
                      <input type="tel" className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" placeholder="+1 (555) 000-0000" data-id="element-1518" />
                    </div>
                    <div className="space-y-2" data-id="element-1519">
                      <label className="text-[10px] font-black uppercase text-[#1B264F]/40" data-id="element-1520">
                        Best Time to Call
                      </label>
                      <select className="w-full bg-white border border-[#D8E2EF] rounded-xl px-4 py-4 focus:outline-none focus:border-[#00A1E0]" data-id="element-1521">
                        <option data-id="element-1522">As soon as possible</option>
                        <option data-id="element-1523">Morning (9AM - 12PM)</option>
                        <option data-id="element-1524">Afternoon (12PM - 5PM)</option>
                        <option data-id="element-1525">Evening (5PM - 8PM)</option>
                      </select>
                    </div>
                    <button className="w-full py-5 bg-[#1B264F] text-white rounded-xl font-black text-lg hover:bg-[#2a3a75] transition-all shadow-xl shadow-[#1B264F]/10" data-id="element-1526">
                      Request Callback
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </motion.section>

          {/* Sign-Up Form */}
          <motion.section initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="py-16 px-8 bg-white" data-id="element-1527">
            <div className="max-w-4xl mx-auto text-center" data-id="element-1528">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#00A1E0]/10 rounded-3xl text-[#00A1E0] mb-8" data-id="element-1529">
                <Shield size={40} data-id="element-1530" />
              </div>
              <h2 className="text-4xl md:text-6xl font-black text-[#1B264F] mb-8 tracking-tighter" data-id="element-1531">
                Ready to become a Boss?
              </h2>
              <p className="text-xl text-[#1B264F]/60 mb-12 font-medium" data-id="element-1532">
                Join 10,000+ top-performing agents who have scaled their
                business with Agenttoboss.
              </p>

              <div className="bg-[#F3F6F9] p-2 rounded-2xl border border-[#D8E2EF] flex flex-col sm:flex-row gap-2 max-w-2xl mx-auto shadow-inner" data-id="element-1533">
                <input type="email" placeholder="Enter your work email" className="flex-1 bg-transparent px-6 py-4 text-[#1B264F] font-bold focus:outline-none placeholder:text-[#1B264F]/30" data-id="element-1534" />
                <button className="px-8 py-4 bg-[#00A1E0] text-white rounded-xl font-black hover:bg-[#0089C4] transition-all shadow-lg shadow-[#00A1E0]/20 whitespace-nowrap" data-id="element-1535">
                  One-Click Sign Up
                </button>
              </div>
              <p className="mt-6 text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest" data-id="element-1536">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </motion.section>
        </motion.div>}

      {/* Shared Footer for all storefront views */}
      <footer className="bg-[#F3F6F9] border-t border-[#D8E2EF] py-20 px-8 mt-20" data-id="element-1537">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-12" data-id="element-1538">
          <div className="col-span-2 md:col-span-1" data-id="element-1539">
            <div className="flex items-center gap-2 mb-6 cursor-pointer" onClick={() => setView('home')} data-id="element-1540">
              <div className="w-8 h-8 bg-[#00A1E0] rounded flex items-center justify-center text-white" data-id="element-1541">
                <Zap size={18} fill="currentColor" data-id="element-1542" />
              </div>
              <span className="text-lg font-black tracking-tight text-[#1B264F]" data-id="element-1543">
                Agent<span className="text-[#00A1E0]" data-id="element-1544">to</span>boss
              </span>
            </div>
            <p className="text-sm text-[#1B264F]/60 font-medium leading-relaxed" data-id="element-1545">
              The definitive command center for the modern enterprise agent.
              Built on the principles of scale and dominance.
            </p>
          </div>

          <div data-id="element-1546">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-1547">
              Platform
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-1548">
              <li data-id="element-1549">
                <button onClick={() => setView('crm-core')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1550">
                  CRM Core
                </button>
              </li>
              <li data-id="element-1551">
                <button onClick={() => setView('automations')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1552">
                  Automations
                </button>
              </li>
              <li data-id="element-1553">
                <button onClick={() => setView('analytics')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1554">
                  Analytics
                </button>
              </li>
              <li data-id="element-1555">
                <button onClick={() => setView('integrations')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1556">
                  Integrations
                </button>
              </li>
            </ul>
          </div>

          <div data-id="element-1557">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-1558">
              Company
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-1559">
              <li data-id="element-1560">
                <button onClick={() => setView('about')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1561">
                  About Us
                </button>
              </li>
              <li data-id="element-1562">
                <button onClick={() => setView('careers')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1563">
                  Careers
                </button>
              </li>
              <li data-id="element-1564">
                <button onClick={() => setView('press')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1565">
                  Press Kit
                </button>
              </li>
              <li data-id="element-1566">
                <button onClick={() => setView('contact')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1567">
                  Contact
                </button>
              </li>
            </ul>
          </div>

          <div data-id="element-1568">
            <h4 className="text-xs font-black uppercase tracking-[0.2em] text-[#1B264F] mb-6" data-id="element-1569">
              Legal
            </h4>
            <ul className="space-y-4 text-sm font-bold text-[#1B264F]/60" data-id="element-1570">
              <li data-id="element-1571">
                <button onClick={() => setView('privacy')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1572">
                  Privacy Policy
                </button>
              </li>
              <li data-id="element-1573">
                <button onClick={() => setView('terms')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1574">
                  Terms of Service
                </button>
              </li>
              <li data-id="element-1575">
                <button onClick={() => setView('security')} className="hover:text-[#00A1E0] transition-colors" data-id="element-1576">
                  Security
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-20 pt-8 border-t border-[#D8E2EF] flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-1577">
          <p className="text-xs font-bold text-[#1B264F]/40 uppercase tracking-widest text-center md:text-left" data-id="element-1578">
            © 2026 Agenttoboss Inc. All rights reserved.
          </p>
          <div className="flex gap-6" data-id="element-1579">
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-1580">
              <Twitter size={18} data-id="element-1581" />
            </a>
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-1582">
              <Linkedin size={18} data-id="element-1583" />
            </a>
            <a href="#" className="w-10 h-10 bg-[#1B264F]/5 hover:bg-[#00A1E0]/10 text-[#1B264F]/40 hover:text-[#00A1E0] rounded-full flex items-center justify-center transition-all" data-id="element-1584">
              <Instagram size={18} data-id="element-1585" />
            </a>
          </div>
        </div>
      </footer>

      {/* Video Modal */}
      <AnimatePresence data-id="element-1586">
        {videoModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#1B264F]/90 backdrop-blur-sm" onClick={() => setVideoModalOpen(false)} data-id="element-1587">
            <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.9,
          opacity: 0
        }} className="relative w-full max-w-5xl aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-1588">
              <button onClick={() => setVideoModalOpen(false)} className="absolute top-6 right-6 z-10 w-12 h-12 bg-white/10 hover:bg-white/20 text-white rounded-full flex items-center justify-center backdrop-blur-md transition-all" data-id="element-1589">
                <X size={24} data-id="element-1590" />
              </button>
              <iframe className="w-full h-full" src="https://www.youtube.com/embed/9Qh4bHqQlnQ?autoplay=1" title="AgentToBoss Platform Overview" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-1591"></iframe>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}